// src/components/Footer.js
import React from "react";
import { Box, Container, Grid, Typography, Divider, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TelegramIcon from '@mui/icons-material/Telegram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { brandColors } from "../config/brandTheme";
import { MERCHANT_LEGAL } from "../config/merchantLegalConfig";
import MerchantLegalNotice from "./MerchantLegalNotice";

const Footer = ({ primaryBlue, glassStyles }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${primaryBlue[900]}, ${primaryBlue[800]})`,
        borderTop: `3px solid ${brandColors.gold}`,
        color: "white",
        py: 8,
        mt: 8,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(45deg, #ffffff, #F0F8FF)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 3,
              }}
            >
              {MERCHANT_LEGAL.brandName}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                opacity: 0.9,
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              Your trusted partner for premium products and exclusive packages
              delivered with excellence.
            </Typography>
            <Box
              sx={{
                ...glassStyles,
                p: 3,
                bgcolor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(15px)",
              }}
            >
              <Box
                sx={{
                  opacity: 0.9,
                  lineHeight: 1.5,
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                <strong>Address:</strong>
                <br />
                {MERCHANT_LEGAL.brandName}
                <br />
                {MERCHANT_LEGAL.addressLines.map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
                <br />
                <strong>GST No:</strong> {MERCHANT_LEGAL.gstin}
              </Box>
            </Box>
          </Grid>

          {/* Customer Service */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "#F0F8FF",
              }}
            >
              Customer Service
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { label: "Help Center", path: "/contact-us" },
                { label: "Track Your Order", path: "/" },
                { label: "Returns & Refunds", path: "/return-policy" },
                { label: "Shipping Information", path: "/return-policy" },
                { label: "Contact Us", path: "/contact-us" },
              ].map((item) => (
                <Typography
                  key={item.label}
                  variant="body1"
                  onClick={() => navigate(item.path)}
                  sx={{
                    opacity: 0.8,
                    cursor: "pointer",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateX(5px)",
                      color: "#F0F8FF",
                    },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Policies */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "#F0F8FF",
              }}
            >
              Policies
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                { label: "Privacy Policy", path: "/privacy-policy" },
                { label: "Terms of Service", path: "/terms-of-service" },
                { label: "Return Policy", path: "/return-policy" },
                { label: "Cancellation Policy", path: "/cancellation-policy" },
                { label: "Security Policy", path: "/privacy-policy" },
              ].map((item) => (
                <Typography
                  key={item.label}
                  variant="body1"
                  onClick={() => navigate(item.path)}
                  sx={{
                    opacity: 0.8,
                    cursor: "pointer",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateX(5px)",
                      color: "#F0F8FF",
                    },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>
          </Grid>

          {/* Connect & Support */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 3,
                color: "#F0F8FF",
              }}
            >
              Connect With Us
            </Typography>
            <Box
              sx={{
                ...glassStyles,
                p: 3,
                mb: 3,
                bgcolor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(15px)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📧 {MERCHANT_LEGAL.supportEmail}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  📞 {MERCHANT_LEGAL.supportPhone}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  🕒 Mon-Sat: 9AM-6PM IST
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 2,
                color: "#F0F8FF",
              }}
            >
              Why Shop With Us?
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {[
                "✓ Free Shipping on orders ₹2,999+",
                "✓ 100% Secure Payments",
                "✓ 7-Day Easy Returns",
              ].map((item) => (
                <Typography
                  key={item}
                  variant="body1"
                  sx={{
                    opacity: 0.9,
                    fontSize: "1rem",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateY(3px)",
                    },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            p: 2.5,
            borderRadius: 2,
            bgcolor: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.15)",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1.5, color: "#F0F8FF", letterSpacing: 0.5 }}
          >
            Contact information
          </Typography>
          <MerchantLegalNotice
            variant="body2"
            sx={{ opacity: 0.9, color: "rgba(255,255,255,0.92)" }}
          />
        </Box>

        <Divider
          sx={{
            my: 6,
            borderColor: "rgba(255, 255, 255, 0.2)",
            borderWidth: 1,
          }}
        />

        {/* Enhanced Bottom Footer */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontWeight: 500,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            © 2025 {MERCHANT_LEGAL.brandName}. All rights reserved.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontWeight: 500,
                mb: { xs: 1, sm: 0 },
              }}
            >
              Follow Us:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <IconButton
                component="a"
                href="https://t.me/Infinitywhale08"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#0088cc",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(0, 136, 204, 0.3)",
                  },
                }}
              >
                <TelegramIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.instagram.com/infinitywhale08/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(225, 48, 108, 0.3)",
                  },
                }}
              >
                <InstagramIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://x.com/InfinityWhale08"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#000000",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
                  },
                }}
              >
                <XIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.linkedin.com/in/infinitywhale08/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#0077b5",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(0, 119, 181, 0.3)",
                  },
                }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.facebook.com/infinitywhale08"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#1877f2",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(24, 119, 242, 0.3)",
                  },
                }}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.youtube.com/@infinitywhale10"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#ff0000",
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 20px rgba(255, 0, 0, 0.3)",
                  },
                }}
              >
                <YouTubeIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
